import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAccessToken } from '../authUtils';
import { useMsal } from '@azure/msal-react';
import {
  COLORS, GRADIENTS, SHADOWS, Icons,
  Btn, ErrorBlock, useToast,
  EmptyState, SkeletonTable, SparkleBadge, StatCard,
} from './ui/crm-ui';
import GenerateDocumentModal, { type PromptForGeneration } from './views/modals/GenerateDocumentModal';
import MlpChatPanel from './MlpChatPanel';

interface PromptTemplate {
  prompt_id: number;
  prompt_name: string;
  category: string | null;
  division: string | null;
  token_sensitivity: string | null;
  has_document_output: boolean;
  output_type: string | null;
  is_conversational?: boolean;
  document_template_id: number | null;
  document_template_name: string | null;
  output_filename_prefix: string | null;
  description: string | null;
  upload_slot_1_label?: string | null;
  upload_slot_2_label?: string | null;
}

interface Props {
  baseApiUrl: string;
}

const ALL_CATEGORIES = '__all__';

const AI_TIPS = [
  'Tip: you can pick up to 20 source documents per generation — Claude reads them all in context.',
  'Tip: any document marked ⚠ will be indexed automatically before generation. No extra clicks.',
  'Tip: the generated workbook is saved straight into the same SharePoint folder as your first source document.',
  'Tip: use the Document Assistance panel on a document page for one-off summaries and Q&A.',
  'Tip: Chat assistants keep context across messages — great for working through a complex matter.',
];

function sensitivityColor(s: string | null): string {
  if (s === 'High') return COLORS.orange;
  if (s === 'Medium') return COLORS.blue;
  return COLORS.green;
}

function sensitivityBg(s: string | null): string {
  if (s === 'High') return COLORS.orangeSoft;
  if (s === 'Medium') return COLORS.blueSoft;
  return COLORS.greenSoft;
}

function greetingWord(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function todayStr(): string {
  return new Date().toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

// Maps a category string to a colored accent + icon + short helper description.
// Falls back gracefully for unknown categories.
function categoryStyle(category: string | null): {
  accent: string;
  accentSoft: string;
  icon: JSX.Element;
  blurb: string;
} {
  const c = (category || '').toLowerCase();
  if (c.includes('audit')) {
    return { accent: COLORS.cyan, accentSoft: COLORS.cyanSoft, icon: Icons.docs(COLORS.cyan), blurb: 'Generates audit working papers and supporting analyses.' };
  }
  if (c.includes('tax')) {
    return { accent: COLORS.green, accentSoft: COLORS.greenSoft, icon: Icons.docs(COLORS.green), blurb: 'Drafts tax correspondence, summaries and lodgement workpapers.' };
  }
  if (c.includes('advisor')) {
    return { accent: COLORS.purple, accentSoft: COLORS.purpleSoft, icon: Icons.prompts(COLORS.purple), blurb: 'Produces advisory letters, recommendations and client reports.' };
  }
  if (c.includes('wealth')) {
    return { accent: COLORS.orange, accentSoft: COLORS.orangeSoft, icon: Icons.prompts(COLORS.orange), blurb: 'Wealth management documents and client communications.' };
  }
  if (c.includes('chat') || c.includes('assist')) {
    return { accent: COLORS.purple, accentSoft: COLORS.purpleSoft, icon: Icons.agent(COLORS.purple), blurb: 'Conversational assistant — chat through a matter step by step.' };
  }
  return { accent: COLORS.blue, accentSoft: COLORS.blueSoft, icon: Icons.prompts(COLORS.blue), blurb: 'AI-generated document built from your selected source files.' };
}

export default function PromptsView({ baseApiUrl }: Props) {
  const [prompts, setPrompts] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activePrompt, setActivePrompt] = useState<PromptForGeneration | null>(null);
  const [activeChatPrompt, setActiveChatPrompt] = useState<{ prompt_id: number; prompt_name: string } | null>(null);
  const [chatWidth, setChatWidth] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>(ALL_CATEGORIES);
  const [search, setSearch] = useState('');
  const [tipIndex, setTipIndex] = useState(() => Math.floor(Math.random() * AI_TIPS.length));
  const [pendingBanner, setPendingBanner] = useState(false);
  const [pendingDispatchId, setPendingDispatchId] = useState<number | null>(null);

  const { show, node: toastNode } = useToast();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const userName = accounts[0]?.name || accounts[0]?.username || '';
  const firstName = userName.split(' ')[0] || '';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get<PromptTemplate[]>(`${baseApiUrl}/api/ai/prompts`)
      .then((res) => { if (!cancelled) setPrompts(res.data); })
      .catch((err: { response?: { data?: { error?: string } }; message: string }) => {
        if (!cancelled) setError(err.response?.data?.error ?? err.message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [baseApiUrl]);

  // Rotate the AI tip every 9 seconds.
  useEffect(() => {
    const id = setInterval(() => setTipIndex((i) => (i + 1) % AI_TIPS.length), 9000);
    return () => clearInterval(id);
  }, []);

  // Derive unique categories sorted alphabetically (case-insensitive), with empty/null grouped as "Other".
  const categories = useMemo(() => {
    const set = new Set<string>();
    prompts.forEach((p) => set.add((p.category && p.category.trim()) || 'Other'));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [prompts]);

  // Stats — all derived from local data (no extra API calls).
  const stats = useMemo(() => {
    const total = prompts.length;
    const generators = prompts.filter((p) => p.has_document_output && p.document_template_id != null).length;
    const chats = prompts.filter((p) => !!p.is_conversational).length;
    return { total, generators, chats };
  }, [prompts]);

  // Apply search + category filter.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return prompts.filter((p) => {
      const cat = (p.category && p.category.trim()) || 'Other';
      if (activeCategory !== ALL_CATEGORIES && cat !== activeCategory) return false;
      if (!q) return true;
      return (
        p.prompt_name.toLowerCase().includes(q) ||
        (p.category ?? '').toLowerCase().includes(q) ||
        (p.division ?? '').toLowerCase().includes(q)
      );
    });
  }, [prompts, activeCategory, search]);

  // Group filtered prompts by category — only when "All" is active. Otherwise return a single bucket.
  const groups = useMemo(() => {
    if (activeCategory !== ALL_CATEGORIES) {
      return [{ name: activeCategory, items: filtered }];
    }
    const map = new Map<string, PromptTemplate[]>();
    filtered.forEach((p) => {
      const cat = (p.category && p.category.trim()) || 'Other';
      const list = map.get(cat) ?? [];
      list.push(p);
      map.set(cat, list);
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, items]) => ({ name, items }));
  }, [filtered, activeCategory]);

  function startResize(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault();
    const el = (e.currentTarget as HTMLElement).parentElement!;
    const startX = e.clientX;
    const startWidth = el.getBoundingClientRect().width;
    const onMove = (ev: MouseEvent) => {
      const newW = Math.max(380, Math.min(window.innerWidth * 0.95, startWidth + (ev.clientX - startX)));
      setChatWidth(newW);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  function openGenerate(p: PromptTemplate) {
    const isBudget = p.prompt_name.toLowerCase().includes('budget');
    // Budget Analysis produces Excel output but uses uploaded files rather than a
    // document_templates sheet config, so it has no required template row.
    if (!p.has_document_output || (!p.document_template_id && !isBudget)) return;
    setActivePrompt({
      prompt_id: p.prompt_id,
      prompt_name: p.prompt_name,
      output_filename_prefix: p.output_filename_prefix,
      upload_slot_1_label: p.upload_slot_1_label,
      upload_slot_2_label: p.upload_slot_2_label,
    });
  }

  function handleSubmitted(dispatchId: number) {
    setPendingDispatchId(dispatchId);
    setPendingBanner(true);
  }

  useEffect(() => {
    if (!pendingBanner || !pendingDispatchId) return;
    const check = async () => {
      try {
        const token = await getAccessToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const { data } = await axios.get<{ status: string; error_message: string | null }>(
          `${baseApiUrl}/api/dispatches/${pendingDispatchId}`,
          { headers },
        );
        if (data.status === 'complete') {
          setPendingBanner(false);
          setPendingDispatchId(null);
          show('Document saved to SharePoint');
        } else if (data.status === 'failed') {
          setPendingBanner(false);
          setPendingDispatchId(null);
          show(data.error_message ?? 'Generation failed');
        } else if (data.status === 'cancelled') {
          setPendingBanner(false);
          setPendingDispatchId(null);
        }
      } catch {
        // Network error — keep polling
      }
    };
    check();
    const id = setInterval(check, 10_000);
    return () => clearInterval(id);
  }, [pendingBanner, pendingDispatchId, baseApiUrl, show]);

  // Close chat modal on Escape
  useEffect(() => {
    if (!activeChatPrompt) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setActiveChatPrompt(null); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [activeChatPrompt]);

  const totalMatching = filtered.length;
  const hasActiveFilter = activeCategory !== ALL_CATEGORIES || search.trim().length > 0;

  return (
    <div className="crm-anim-fadeUp">
      {/* ── Pending generation banner ───────────────────────────────────── */}
      {pendingBanner && (
        <div style={{
          position: 'relative', marginBottom: 20,
          background: COLORS.white,
          border: `1px solid ${COLORS.purple}50`,
          borderLeft: `4px solid ${COLORS.purple}`,
          borderRadius: 12,
          boxShadow: `0 4px 18px rgba(124,58,237,0.13), 0 1px 4px rgba(0,0,0,0.06)`,
        }}>
          {/* Clickable area — navigates to Dispatches */}
          <button
            type="button"
            onClick={() => navigate('/dispatches')}
            style={{
              display: 'flex', alignItems: 'center', gap: 14,
              width: '100%', padding: '16px 52px 16px 18px',
              background: 'none', border: 'none', cursor: 'pointer',
              textAlign: 'left', fontFamily: 'inherit', borderRadius: 12,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f3ff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
          >
            <div style={{
              width: 40, height: 40, borderRadius: 10, flexShrink: 0,
              background: GRADIENTS.ai,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: SHADOWS.brand,
            }}>
              <span className="crm-anim-pulseSoft">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
              </span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.dark, marginBottom: 2 }}>
                Generating your document…
              </div>
              <div style={{ fontSize: 12, color: COLORS.mid }}>
                This may take a few minutes. The{' '}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                  stroke={COLORS.purple} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  style={{ display: 'inline', verticalAlign: 'middle', marginBottom: 1 }}>
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {' '}bell icon in the top bar will notify you when it's saved to SharePoint. Click to view all dispatches.
              </div>
            </div>
          </button>
          {/* Dismiss — sits outside the clickable button to avoid nesting */}
          <button
            type="button"
            onClick={() => setPendingBanner(false)}
            style={{
              position: 'absolute', top: '50%', right: 12,
              transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer',
              color: COLORS.mid, fontSize: 18, lineHeight: 1, padding: '4px 6px',
              borderRadius: 6,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.light; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
            aria-label="Dismiss"
          >×</button>
        </div>
      )}

      {/* ── Hero band ───────────────────────────────────────────────────── */}
      <div
        style={{
          position: 'relative',
          background: GRADIENTS.aiSoft,
          borderRadius: 16,
          padding: '28px 28px 22px',
          marginBottom: 20,
          border: `1px solid ${COLORS.purple}1f`,
          overflow: 'hidden',
        }}
      >
        {/* Decorative gradient stripe down the left edge */}
        <div className="crm-gradient-ai" style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: 4,
        }} />
        {/* Drifting sparkles in the background */}
        <div style={{ position: 'absolute', top: 22, right: 36, color: COLORS.purple, opacity: 0.55 }}>
          <SparkleBadge size={28} />
        </div>
        <div style={{ position: 'absolute', top: 80, right: 110, color: COLORS.blue, opacity: 0.35 }}>
          <SparkleBadge size={18} />
        </div>
        <div style={{ position: 'absolute', bottom: 26, right: 70, color: COLORS.purple, opacity: 0.35 }}>
          <SparkleBadge size={14} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, position: 'relative', zIndex: 1 }}>
          <div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '3px 10px', borderRadius: 999,
              background: COLORS.white, color: COLORS.purple,
              fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
              boxShadow: SHADOWS.sm, marginBottom: 12,
            }}>
              {Icons.sparkle(COLORS.purple, 12)} POWERED BY CLAUDE
            </div>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: COLORS.dark, margin: 0, lineHeight: 1.2 }}>
              {greetingWord()}{firstName ? `, ${firstName}` : ''}
            </h1>
            <div style={{ fontSize: 13, color: COLORS.mid, marginTop: 4 }}>
              {todayStr()} · What would you like to generate today?
            </div>
          </div>

          {/* Search input */}
          <div className="crm-focus-ring" style={{
            background: COLORS.white,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 12,
            display: 'flex', alignItems: 'center',
            padding: '0 14px',
            boxShadow: SHADOWS.sm,
            maxWidth: 560,
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          }}>
            <span style={{ lineHeight: 0, marginRight: 10, color: COLORS.mid }}>{Icons.search(COLORS.mid)}</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Find a prompt…"
              style={{
                flex: 1, padding: '12px 0', border: 'none', outline: 'none',
                fontSize: 14, background: 'transparent', fontFamily: 'inherit', color: COLORS.dark,
              }}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: COLORS.mid, lineHeight: 0 }}
                aria-label="Clear search"
              >
                {Icons.close(COLORS.mid)}
              </button>
            )}
          </div>

          {/* Rotating AI tip */}
          <div
            key={tipIndex}
            className="crm-anim-fadeIn"
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 12, color: COLORS.mid, fontStyle: 'italic',
              maxWidth: 720,
            }}
          >
            <span className="crm-anim-pulseSoft" style={{ color: COLORS.purple, lineHeight: 0 }}>
              {Icons.sparkle(COLORS.purple, 12)}
            </span>
            {AI_TIPS[tipIndex]}
          </div>
        </div>
      </div>

      {/* ── Stats row ───────────────────────────────────────────────────── */}
      {!loading && !error && prompts.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <StatCard label="Prompts available" value={stats.total} accent={COLORS.blue} />
          <StatCard label="Document generators" value={stats.generators} sub="Produce structured output" accent={COLORS.green} />
          <StatCard label="Chat assistants" value={stats.chats} sub="Conversational AI helpers" accent={COLORS.purple} />
        </div>
      )}

      {/* ── Error / loading ─────────────────────────────────────────────── */}
      {error && <ErrorBlock message={error} />}
      {loading && <SkeletonTable rows={4} widths={[40, 18, 18, 14]} />}

      {!loading && !error && prompts.length === 0 && (
        <EmptyState
          icon={Icons.prompts(COLORS.blue)}
          title="No prompts available yet"
          description="Once prompts are configured, they will appear here for you to run."
        />
      )}

      {/* ── Filter chips ────────────────────────────────────────────────── */}
      {!loading && !error && prompts.length > 0 && (
        <div style={{
          display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16,
        }}>
          <FilterChip
            label="All"
            active={activeCategory === ALL_CATEGORIES}
            count={prompts.length}
            onClick={() => setActiveCategory(ALL_CATEGORIES)}
          />
          {categories.map((c) => (
            <FilterChip
              key={c}
              label={c}
              active={activeCategory === c}
              count={prompts.filter((p) => ((p.category && p.category.trim()) || 'Other') === c).length}
              onClick={() => setActiveCategory(c)}
            />
          ))}
          {hasActiveFilter && (
            <button
              type="button"
              onClick={() => { setActiveCategory(ALL_CATEGORIES); setSearch(''); }}
              style={{
                marginLeft: 4, background: 'none', border: 'none', cursor: 'pointer',
                color: COLORS.mid, fontSize: 12, padding: '4px 8px', fontFamily: 'inherit',
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* ── Empty filter result ─────────────────────────────────────────── */}
      {!loading && !error && prompts.length > 0 && totalMatching === 0 && (
        <EmptyState
          icon={Icons.search(COLORS.blue)}
          title="No prompts match your filters"
          description="Try a different category or clear the search."
          compact
        />
      )}

      {/* ── Grouped sections of prompt cards ────────────────────────────── */}
      {!loading && !error && groups.map((g) => (
        g.items.length === 0 ? null : (
          <div key={g.name} style={{ marginBottom: 26 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.mid, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                {g.name}
              </div>
              <span style={{
                fontSize: 11, fontWeight: 600, color: COLORS.blue, background: COLORS.blueSoft,
                padding: '1px 8px', borderRadius: 999,
              }}>{g.items.length}</span>
            </div>
            <div
              className="crm-stagger"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 14,
              }}
            >
              {g.items.map((p) => (
                <PromptCard
                  key={p.prompt_id}
                  prompt={p}
                  onGenerate={() => openGenerate(p)}
                  onChat={() => setActiveChatPrompt({ prompt_id: p.prompt_id, prompt_name: p.prompt_name })}
                />
              ))}
            </div>
          </div>
        )
      ))}

      {activeChatPrompt && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(15,20,50,0.55)',
            display: 'flex', alignItems: 'stretch', justifyContent: 'center',
            padding: 24,
            backdropFilter: 'blur(2px)',
          }}
        >
          <div style={{ width: chatWidth ?? '66vw', minWidth: 380, maxWidth: '95vw', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            <MlpChatPanel
              promptId={activeChatPrompt.prompt_id}
              promptName={activeChatPrompt.prompt_name}
              baseApiUrl={baseApiUrl}
              onClose={() => setActiveChatPrompt(null)}
            />
            {/* Drag handle on right edge */}
            <div
              onMouseDown={startResize}
              title="Drag to resize"
              style={{
                position: 'absolute', right: -5, top: 0, bottom: 0, width: 10,
                cursor: 'ew-resize', zIndex: 10,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <div style={{
                width: 4, height: 40, borderRadius: 4,
                background: 'rgba(124,58,237,0.35)',
              }} />
            </div>
          </div>
        </div>,
        document.body,
      )}

      <GenerateDocumentModal
        baseApiUrl={baseApiUrl}
        open={activePrompt !== null}
        prompt={activePrompt}
        onClose={() => setActivePrompt(null)}
        onSuccess={() => {}}
        onSubmitted={handleSubmitted}
      />

      {toastNode}
    </div>
  );
}

// ─── Filter chip ────────────────────────────────────────────────────────────

const FilterChip: React.FC<{
  label: string;
  active: boolean;
  count?: number;
  onClick: () => void;
}> = ({ label, active, count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '6px 12px', borderRadius: 999,
      border: `1px solid ${active ? COLORS.blue : COLORS.border}`,
      background: active ? COLORS.blue : COLORS.white,
      color: active ? '#fff' : COLORS.dark,
      fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
      cursor: 'pointer', transition: 'all 0.12s',
      boxShadow: active ? SHADOWS.sm : 'none',
    }}
  >
    {label}
    {count != null && (
      <span style={{
        fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999,
        background: active ? 'rgba(255,255,255,0.18)' : COLORS.light,
        color: active ? '#fff' : COLORS.mid,
      }}>{count}</span>
    )}
  </button>
);

// ─── Prompt card ────────────────────────────────────────────────────────────

const PromptCard: React.FC<{
  prompt: PromptTemplate;
  onGenerate: () => void;
  onChat: () => void;
}> = ({ prompt: p, onGenerate, onChat }) => {
  const canGenerate = p.has_document_output && p.document_template_id != null;
  const isChat = !!p.is_conversational;
  const cat = categoryStyle(p.category);
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="crm-card-lift"
      style={{
        position: 'relative',
        background: COLORS.white,
        borderRadius: 14,
        border: `1px solid ${COLORS.border}`,
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        boxShadow: SHADOWS.sm,
        overflow: 'hidden',
        minHeight: 200,
      }}
    >
      {/* Accent stripe down the left edge */}
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
        background: cat.accent, opacity: 0.85,
      }} />

      {/* Header: icon tile + name */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 11,
          background: cat.accentSoft, color: cat.accent,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, boxShadow: SHADOWS.sm,
        }}>
          {cat.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 700, color: COLORS.dark,
            lineHeight: 1.3,
          }}>{p.prompt_name}</div>
          {p.category && (
            <div style={{ fontSize: 11, color: COLORS.mid, marginTop: 3 }}>
              {p.category}
            </div>
          )}
        </div>
      </div>

      {/* Blurb */}
      <div>
        <div style={{
          fontSize: 12, color: COLORS.mid, lineHeight: 1.5,
          whiteSpace: expanded ? 'pre-wrap' : undefined,
          overflow: expanded ? undefined : 'hidden',
          display: expanded ? undefined : '-webkit-box',
          WebkitLineClamp: expanded ? undefined : 2,
          WebkitBoxOrient: expanded ? undefined : 'vertical',
        }}>
          {p.description || cat.blurb}
        </div>
        {p.description && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: COLORS.blue, fontSize: 11, fontWeight: 600,
              padding: '2px 0', marginTop: 2, fontFamily: 'inherit',
            }}
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>

      {/* Chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {p.division && (
          <span style={{
            fontSize: 10, padding: '2px 8px', borderRadius: 5,
            background: COLORS.light, color: COLORS.mid, fontWeight: 600,
          }}>{p.division}</span>
        )}
        {p.token_sensitivity && (
          <span style={{
            fontSize: 10, padding: '2px 8px', borderRadius: 5,
            background: sensitivityBg(p.token_sensitivity),
            color: sensitivityColor(p.token_sensitivity),
            fontWeight: 600,
          }}>{p.token_sensitivity} sensitivity</span>
        )}
        {canGenerate && (
          <span style={{
            fontSize: 10, padding: '2px 8px', borderRadius: 5,
            background: COLORS.greenSoft, color: COLORS.green, fontWeight: 600,
          }}>{(p.output_type ?? 'document').toUpperCase()} output</span>
        )}
        {isChat && !canGenerate && (
          <span style={{
            fontSize: 10, padding: '2px 8px', borderRadius: 5,
            background: COLORS.purpleSoft, color: COLORS.purple, fontWeight: 600,
          }}>CHAT</span>
        )}
      </div>

      {/* Action */}
      <div style={{ marginTop: 'auto', display: 'flex', gap: 8 }}>
        {canGenerate ? (
          <Btn
            primary
            small
            onClick={onGenerate}
            style={{
              background: GRADIENTS.ai,
              border: 'none',
              boxShadow: SHADOWS.brand,
              flex: 1,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
              {Icons.sparkle('#fff', 13)} Generate
            </span>
          </Btn>
        ) : isChat ? (
          <Btn
            primary
            small
            onClick={onChat}
            style={{
              background: GRADIENTS.ai,
              border: 'none',
              boxShadow: SHADOWS.brand,
              flex: 1,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
              {Icons.agent('#fff')} Start Chat
            </span>
          </Btn>
        ) : (
          <span style={{ fontSize: 11, color: COLORS.mid, fontStyle: 'italic' }}>
            Run via Document Assistance panel
          </span>
        )}
      </div>
    </div>
  );
};

